import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SnakeBlock from '../SnakeBlock';
import ConnectorBlock from '../ConnectorBlock';
import EmptyBlock from '../EmptyBlock';
import './Tutorial.css';

// Delays tuned so typing finishes before the next phase, with extra reading time.
// Typing speed: 100ms/char. "Catch Me If You Can"=19ch, "Brad Pitt"=9ch, "Leonardo DiCaprio"=17ch.
const DELAYS = [2500, 3000, 1800, 2500, 1800, 1800, 3000, 3000, 1800, 2500];

const TOM_HANKS         = { id: 15713, name: 'Tom Hanks',               image: '/xndWFsBlClOJFRdhSt4NBwiPq2o.jpg', date_of_birth: '1956-07-09', select_count: 174, type: 'ACTOR' };
const CATCH_ME          = { id: 3818,  title: 'Catch Me If You Can',    name: 'Catch Me If You Can',    image: '/ctjEj2xM32OvBXCq8zAdK3ZrsAj.jpg', year: 2002, select_count: 74,  type: 'MEDIA' };
const BRAD_PITT         = { id: 24323, name: 'Brad Pitt',               image: '/cckcYc2v0yh1tc9QjRelptcOBko.jpg', date_of_birth: '1963-12-18', select_count: 357, type: 'ACTOR', strike: true };
const LEONARDO_DICAPRIO = { id: 53684, name: 'Leonardo DiCaprio',       image: '/wo2hJpn04vbtmh0B9utCFdsQhxM.jpg', date_of_birth: '1974-11-11', select_count: 371, type: 'ACTOR' };
const WOLF_OF_WALL_ST   = { id: 30456, title: 'The Wolf of Wall Street', name: 'The Wolf of Wall Street', image: '/34m2tygAYBGqA9MXKhRDtzYd4MR.jpg', year: 2013, select_count: 162, type: 'MEDIA' };

const SEARCH_TARGETS = { 1: 'Catch Me If You Can', 4: 'Brad Pitt', 7: 'Leonardo DiCaprio' };

const CAPTIONS = {
    0:  'Connect the head to the tail — alternate between actors and movies.',
    1:  'Search for a movie that Tom Hanks appeared in…',
    2:  'Select the result.',
    3:  '✓ Tom Hanks starred in Catch Me If You Can!',
    4:  'Now search for an actor who appeared in Catch Me If You Can…',
    5:  'Pick a result from the list.',
    6:  "✗ Brad Pitt wasn't in Catch Me If You Can, that's a strike! You have 3 strikes total.",
    7:  'Try again…',
    8:  'Select the correct result.',
    9:  '✓ Leonardo DiCaprio was in Catch Me If You Can and Wolf of Wallstreet!',
    10: 'Nice work! You connected Tom Hanks to The Wolf of Wall Street.',
};

const imgUrl = (path) => `https://image.tmdb.org/t/p/w300_and_h450_bestv2/${path}`;

export default function TutorialModal({ show, onClose, isMobile }) {
    const [phase, setPhase] = useState(0);
    const [strikeAnimating, setStrikeAnimating] = useState(false);
    const [typedText, setTypedText] = useState('');
    const boardRef = useRef(null);
    const captionMobileRef = useRef(null);
    const ctaRef = useRef(null);

    useEffect(() => {
        if (show) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }
        return () => document.body.classList.remove('modal-open');
    }, [show]);

    useEffect(() => {
        if (!show) return;
        setPhase(0);
        setTypedText('');
        setStrikeAnimating(false);

        let elapsed = 0;
        const timeouts = DELAYS.map((delay, i) => {
            elapsed += delay;
            return setTimeout(() => {
                const next = i + 1;
                setPhase(next);
                if (next === 6) {
                    setStrikeAnimating(true);
                    setTimeout(() => setStrikeAnimating(false), 600);
                }
            }, elapsed);
        });

        return () => timeouts.forEach(clearTimeout);
    }, [show]);

    useEffect(() => {
        const target = SEARCH_TARGETS[phase];
        if (!target) {
            if ([0, 3, 6, 9, 10].includes(phase)) setTypedText('');
            return;
        }
        setTypedText('');
        let i = 0;
        const interval = setInterval(() => {
            i++;
            setTypedText(target.slice(0, i));
            if (i >= target.length) clearInterval(interval);
        }, 100);
        return () => clearInterval(interval);
    }, [phase]);

    useEffect(() => {
        if (isMobile) {
            if (phase === 10 && ctaRef.current) {
                ctaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } else if (captionMobileRef.current) {
                captionMobileRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        } else {
            if ((phase === 3 || phase === 9) && boardRef.current) {
                boardRef.current.scrollLeft = boardRef.current.scrollWidth;
            }
        }
    }, [phase, isMobile]);

    if (!show) return null;

    const strikeCount  = phase >= 6 ? 1 : 0;
    const showCatchMe  = phase >= 3;
    const showWrong    = phase === 6;
    const showLeonardo = phase >= 9;
    const searchType   = phase < 3 ? 'MEDIA' : phase < 9 ? 'ACTOR' : 'MEDIA';

    // Mobile: search bar lives in the chain (not below); hidden during the wrong-guess flash
    const showSearchInChain = isMobile && !showWrong && phase <= 9;
    // Desktop: search bar lives below the board
    const showSearchBelow   = !isMobile && phase <= 9;

    const suggestion = phase === 2 ? CATCH_ME
                     : phase === 5 ? BRAD_PITT
                     : phase === 8 ? LEONARDO_DICAPRIO
                     : null;

    const fakeSearchBar = (
        <div className="search-bar-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <input
                    type="text"
                    readOnly
                    value={typedText}
                    className="search-input"
                    placeholder={`Search for ${searchType !== 'MEDIA' ? 'actor or actress' : 'movie'}`}
                />
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 20 }}>
                    <FontAwesomeIcon size="lg" icon={['fas', 'lightbulb']} color="#ccc" />
                    <FontAwesomeIcon size="lg" style={{ transform: 'rotate(90deg)' }} icon={['fas', 'arrow-right-arrow-left']} color="#ccc" />
                </div>
            </div>
            {suggestion && (
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', overflowX: 'scroll', gap: 10, padding: 10 }}>
                    <div style={{ backgroundColor: 'black', opacity: 0.8, padding: 10, borderRadius: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}>
                        <img style={{ height: 140 }} src={imgUrl(suggestion.image)} alt={suggestion.name} />
                        <p style={{ color: 'white' }}>{suggestion.name}</p>
                        <p style={{ color: 'white' }}>{suggestion.year || suggestion.date_of_birth?.split('-')[0]}</p>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="modal-overlay">
            <div className="tutorial-modal-content">

                <div className="tutorial-close-bar" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                    <span
                        onClick={onClose}
                        style={{ cursor: 'pointer', fontSize: 13, color: '#888', userSelect: 'none' }}
                    >
                        Skip
                    </span>
                    <FontAwesomeIcon
                        icon="times"
                        onClick={onClose}
                        color="black"
                        style={{ cursor: 'pointer', borderRadius: '50%', backgroundColor: 'red', paddingTop: 3, paddingBottom: 3, paddingLeft: 5, paddingRight: 5 }}
                    />
                </div>

                <h2 className="tutorial-title">How to Play</h2>

                <div
                    ref={boardRef}
                    className={`tutorial-board ${isMobile ? 'tutorial-board--vertical' : 'tutorial-board--horizontal'}`}
                >
                    {/* Tom Hanks — always first */}
                    <SnakeBlock info={TOM_HANKS} showGoBack={() => {}} />
                    <div className="tutorial-connector"><ConnectorBlock isMobile={isMobile} /></div>

                    {/* Catch Me If You Can — slides in at phase 3 */}
                    {showCatchMe && <>
                        <div className="tutorial-block-enter">
                            <SnakeBlock info={CATCH_ME} showGoBack={() => {}} />
                        </div>
                        <div className="tutorial-connector"><ConnectorBlock isMobile={isMobile} /></div>
                    </>}

                    {/*
                     * The "open slot" — exactly one of these renders at a time:
                     *   showWrong    → Brad Pitt (red flash, then fades)
                     *   showLeonardo → Leonardo DiCaprio (correct, slides in)
                     *   otherwise   → SearchBar (mobile, inline) or EmptyBlock (desktop)
                     * A single connector after this slot leads to Wolf of Wall Street.
                     */}
                    {/* Caption lives here on mobile — just above the search bar / open slot */}
                    {isMobile && (
                        <p ref={captionMobileRef} key={phase} className="tutorial-caption" style={{ width: '100%', marginTop: 4, marginBottom: 4 }}>{CAPTIONS[phase]}</p>
                    )}

                    {showWrong && (
                        <div className="tutorial-wrong-wrap">
                            <SnakeBlock info={BRAD_PITT} showGoBack={() => {}} />
                        </div>
                    )}

                    {!showWrong && showLeonardo && (
                        <div className="tutorial-block-enter">
                            <SnakeBlock info={LEONARDO_DICAPRIO} showGoBack={() => {}} />
                        </div>
                    )}

                    {!showWrong && !showLeonardo && showSearchInChain && (
                        <div style={{ width: '100%', marginTop: 10, marginBottom: 10 }}>
                            {fakeSearchBar}
                        </div>
                    )}

                    {!showWrong && !showLeonardo && !isMobile && (
                        <EmptyBlock media={searchType} />
                    )}

                    {/* Single connector into Wolf of Wall Street */}
                    <div className="tutorial-connector"><ConnectorBlock isMobile={isMobile} /></div>
                    <SnakeBlock info={WOLF_OF_WALL_ST} showGoBack={() => {}} />
                </div>

                {/* Strike counter — matches the real Header style */}
                <div className={`tutorial-strikes${strikeAnimating ? ' tutorial-strikes--bounce' : ''}`}>
                    {[0, 1, 2].map(i => (
                        <FontAwesomeIcon
                            key={i}
                            icon={['fas', 'times']}
                            size="xl"
                            color={i < strikeCount ? 'red' : '#555'}
                        />
                    ))}
                </div>

                {/* Desktop: search bar below the board */}
                {showSearchBelow && fakeSearchBar}

                {/* Caption — desktop only (mobile caption is inside the board above the search bar) */}
                {!isMobile && <p key={phase} className="tutorial-caption">{CAPTIONS[phase]}</p>}

                {phase >= 10 && (
                    <button ref={ctaRef} className="tutorial-cta" onClick={onClose}>
                        Let's Play →
                    </button>
                )}
            </div>
        </div>
    );
}
